
import { useState, FormEvent } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const { ref, isVisible } = useScrollAnimation();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    if (formData.message.length > 500) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      return;
    }

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('company', formData.company);
      formBody.append('email', formData.email);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6alcufmvg9ih2c7am8g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', company: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section ref={ref} id="contact" className="bg-gray-50 py-32 lg:py-40 px-8">
      <div className="max-w-3xl mx-auto text-center">
        <div
          className={`transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-5xl lg:text-6xl font-light text-gray-900 mb-10">Contact</h2>

          <div className="mb-16">
            <p className="text-base leading-relaxed text-gray-700 mb-4">
              事業のご相談、協業、取材のご連絡はこちらからお願いします。
            </p>
            <p className="text-sm leading-relaxed text-gray-600">
              For inquiries, partnerships, or press requests, please contact us.
            </p>
          </div>
        </div>

        {submitStatus === 'success' ? (
          <div
            className={`bg-white rounded-xl shadow-sm p-16 max-w-2xl mx-auto transition-all duration-700 delay-100 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-8 bg-gray-900 rounded-full">
                <i className="ri-check-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-medium text-gray-900 mb-4">送信が完了しました</h3>
              <p className="text-base text-gray-600 leading-relaxed mb-3">
                お問い合わせいただきありがとうございます。
              </p>
              <p className="text-sm text-gray-500 leading-relaxed mb-3">
                内容を確認の上、通常2営業日以内にご連絡いたします。
              </p>
              <p className="text-sm text-gray-400 leading-relaxed mb-8">
                Thank you for your inquiry. We will respond within 2 business days.
              </p>

              <div className="bg-gray-50 rounded-xl p-6 mb-8 text-left">
                <p className="text-xs uppercase tracking-wider text-gray-400 mb-4">
                  送信内容の確認 / Submission Summary
                </p>
                <div className="space-y-3 text-sm">
                  <div className="flex gap-3">
                    <span className="text-gray-400 w-20 shrink-0">Name</span>
                    <span className="text-gray-700">{formData.name || '—'}</span>
                  </div>
                  <div className="flex gap-3">
                    <span className="text-gray-400 w-20 shrink-0">Email</span>
                    <span className="text-gray-700">{formData.email || '—'}</span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => {
                  setSubmitStatus('idle');
                  setFormData({ name: '', company: '', email: '', message: '' });
                }}
                className="px-10 h-12 bg-gray-100 text-gray-700 text-sm font-medium rounded-full hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                新しいお問い合わせ / New Inquiry
              </button>
            </div>
          </div>
        ) : (
          <form
            id="contact-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className={`bg-white rounded-xl shadow-sm p-16 max-w-2xl mx-auto transition-all duration-700 delay-100 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="space-y-6">
              <div className="text-left">
                <label htmlFor="name" className="block text-sm text-gray-600 mb-2">
                  Name / お名前 <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-900 transition-colors text-sm"
                />
              </div>

              <div className="text-left">
                <label htmlFor="company" className="block text-sm text-gray-600 mb-2">
                  Company / 会社名
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-900 transition-colors text-sm"
                />
              </div>

              <div className="text-left">
                <label htmlFor="email" className="block text-sm text-gray-600 mb-2">
                  Email / メールアドレス <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-900 transition-colors text-sm"
                />
              </div>

              <div className="text-left">
                <label htmlFor="message" className="block text-sm text-gray-600 mb-2">
                  Message / お問い合わせ内容 <span className="text-red-400">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-900 transition-colors resize-none text-sm"
                />
                <p className="text-xs text-gray-500 mt-2">
                  {formData.message.length} / 500
                </p>
              </div>
            </div>

            {submitStatus === 'error' && (
              <div className="mt-6 p-4 bg-red-50 text-red-800 rounded-lg text-sm flex items-center gap-3">
                <i className="ri-error-warning-line text-lg"></i>
                <span>送信に失敗しました。入力内容をご確認の上、もう一度お試しください。</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-10 w-52 h-14 bg-gray-900 text-white text-sm font-medium rounded-full hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  送信中...
                </span>
              ) : (
                'Send / 送信'
              )}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
